
import { Component, Vue } from 'vue-property-decorator';
import CameraPageHeader from '@/components/CameraPageHeader.vue';
import CameraPageHeaderPaidy from '@/components/CameraPageHeaderPaidy.vue';
import ModalDefault from '@/components/ModalDefault.vue';
import ModalDefaultPopupBodyErrorNormal from '@/components/ModalDefaultPopupBodyErrorNormal.vue';
import ModalDefaultPopupBodyErrorFaceAction, {
  MessageTypeErrorFaceAction
} from '@/components/ModalDefaultPopupBodyErrorFaceAction.vue';
import { CameraChildErrorCode } from '@/resources/error/CameraChildErrorCode';

@Component({
  components: {
    CameraPageHeader,
    CameraPageHeaderPaidy,
    ModalDefault,
    ModalDefaultPopupBodyErrorNormal,
    ModalDefaultPopupBodyErrorFaceAction
  }
})
export default class CameraChildError extends Vue {
  // クエリからエラー情報を配列で取得
  private get errorFields(): string[] {
    const queryFields: string | (string | null)[] | undefined = this.$route.query.fields;
    if (typeof queryFields === 'string') {
      return [queryFields];
    } else if (Array.isArray(queryFields)) {
      const errorFields: string[] = [];
      for (let i = 0; i < queryFields.length; i++) {
        const queryField: string | null = queryFields[i];
        if (typeof queryField === 'string') errorFields.push(queryField);
      }
      return errorFields;
    } else {
      return [];
    }
  }

  // クエリから首振りのエラーを文字列で取得
  private get enumErrorFaceAction(): MessageTypeErrorFaceAction | null {
    const queryErrorFaceAction: string | (string | null)[] | undefined = this.$route.query.face_action_error;
    if (typeof queryErrorFaceAction !== 'string') return null;
    const numberErrorFaceAction: number = Number.parseInt(queryErrorFaceAction, 10);
    if (numberErrorFaceAction === MessageTypeErrorFaceAction.None) return numberErrorFaceAction;
    if (numberErrorFaceAction === MessageTypeErrorFaceAction.FaceNotFound) return numberErrorFaceAction;
    if (numberErrorFaceAction === MessageTypeErrorFaceAction.DifferentDirection) return numberErrorFaceAction;
    if (numberErrorFaceAction === MessageTypeErrorFaceAction.Timeout) return numberErrorFaceAction;
    if (numberErrorFaceAction === MessageTypeErrorFaceAction.InvalidScreenOrientation) return numberErrorFaceAction;
    if (numberErrorFaceAction === MessageTypeErrorFaceAction.CVInvalidFeatureBrightness) return numberErrorFaceAction;
    if (numberErrorFaceAction === MessageTypeErrorFaceAction.CVInvalidFeatureSharpness) return numberErrorFaceAction;
    if (numberErrorFaceAction === MessageTypeErrorFaceAction.CVInvalidFeatureHighlight) return numberErrorFaceAction;
    if (numberErrorFaceAction === MessageTypeErrorFaceAction.CVInvalidFeatureSunglass) return numberErrorFaceAction;
    if (numberErrorFaceAction === MessageTypeErrorFaceAction.CVInvalidFeatureStraight) return numberErrorFaceAction;
    if (numberErrorFaceAction === MessageTypeErrorFaceAction.CVInvalidFeatureIsGrayscale) return numberErrorFaceAction;
    if (numberErrorFaceAction === MessageTypeErrorFaceAction.Unknown) return numberErrorFaceAction;
    return MessageTypeErrorFaceAction.Unknown;
  }

  private get isCameraChildErrorAutodetectTimeout(): boolean {
    return this.errorFields.indexOf(CameraChildErrorCode.AutodetectTimeout) >= 0;
  }

  private get isFaceActionErrorOrientation(): boolean {
    if (this.$route.name !== 'face-action-error') return false;
    return this.enumErrorFaceAction === MessageTypeErrorFaceAction.InvalidScreenOrientation;
  }

  private get isFaceAction(): boolean {
    return this.$store.state.camera.shootType === 'face-action';
  }

  private get isIdDoc(): boolean {
    return this.$store.getters['camera/isIdDoc'];
  }

  private get isPaidy(): boolean {
    return this.$store.getters['auth/isPaidy'];
  }

  private get shouldSkipDocumentSelection(): boolean {
    return this.$store.getters['auth/pageTransitionMode'] === 1;
  }

  // 画面の傾きが正しいか確認
  private get isInvalidOrientation(): boolean {
    return this.$store.getters['orientation/isInvalidOrientation'];
  }

  private get buttonOK(): string {
    if (this.isFaceActionErrorOrientation && this.isInvalidOrientation) return '';
    if (this.isCameraChildErrorAutodetectTimeout) return this.$t('cameraCameraChildError.toManual') as string;
    if (this.isFaceAction) return this.$t('componentsfacetrackerFaceDirectionModal.retry') as string;
    return this.$t('cameraCameraChildError.restart') as string;
  }

  private get buttonNG(): string {
    if (this.isFaceActionErrorOrientation && this.isInvalidOrientation) return '';
    if (this.shouldSkipDocumentSelection) return '';
    if (this.isCameraChildErrorAutodetectTimeout) return this.$t('cameraCameraChildError.keepAuto') as string;
    if (this.isFaceAction) return this.$t('componentsfacetrackerFaceDirectionModal.help') as string;
    if (this.isIdDoc) return this.$t('202410_001') as string;
    return '';
  }

  public async created(): Promise<void> {
    // ページマウント時の設定
    await this.$store.dispatch('camera/mountedSetting', this.$route.path);
  }

  private onClickOK(): void {
    if (this.isCameraChildErrorAutodetectTimeout) return this.useManual();
    if (this.isFaceAction) return this.onFaceActionRetry();
    return this.onRestart();
  }

  private onClickNG(): void {
    if (this.isCameraChildErrorAutodetectTimeout) return this.keepAuto();
    if (this.isFaceAction) return this.onRestart();
    if (this.isIdDoc) return this.backToIdDocSelection();
  }

  public onRestart(): void {
    if (this.$store.getters['camera/showShootTypeConfirmation']) {
      this.$router.push(this.$store.getters['camera/locationShootTypeConfirmation']);
    } else {
      this.$router.push(this.$store.getters['camera/locationIntroduction']);
    }
  }

  public useManual(): void {
    this.$store.commit('camera/isDetectStop', true);
    this.$router.push(this.$store.getters['camera/locationIntroduction']);
  }

  public keepAuto(): void {
    this.$store.commit('camera/isDetectStop', false);
    this.$router.push(this.$store.getters['camera/locationIntroduction']);
  }

  private onFaceActionRetry() {
    this.$store.commit('camera/setFaceActionSkipLoading', true);
    this.$router.push({ name: 'face-action-shooting' });
  }

  private backToIdDocSelection() {
    this.$store.commit('camera/resetDocTypes');
    this.$router.push('/top/personal-information');
  }
}
